import { useState } from 'react';

import { Bot, ChevronDown, ChevronRight, Cog, User } from 'lucide-react';

import {
  ACTIVITY_ACTION_LABELS,
  ACTIVITY_ENTITY_TYPE_LABELS,
  ACTIVITY_SOURCE_LABELS,
  DESTRUCTIVE_ACTIVITY_ACTIONS,
} from '@repo/shared-types';

import { ActivityDiff } from './ActivityDiff';

import type { ActivityLog } from '@repo/shared-types';

const ACTOR_ICON = {
  user: User,
  mcp: Bot,
  system: Cog,
} as const;

/** MCP entries are tinted differently on purpose — "was it the AI?" is the
 * question this log was built to answer, so it has to be visible at a glance. */
const ACTOR_TONE = {
  user: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  mcp: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  system: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
} as const;

const actionTone = (action: string, status: string) => {
  if (status === 'failed') return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
  if (DESTRUCTIVE_ACTIVITY_ACTIONS.has(action)) return 'bg-red-500/10 text-red-500';
  if (action === 'create' || action === 'create_version')
    return 'bg-emerald-500/10 text-emerald-600';
  return 'bg-muted text-foreground/70';
};

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const actorLabel = (entry: ActivityLog): string => {
  if (entry.actor_type === 'mcp') return 'MCP / AI tooling';
  if (entry.actor_name) return entry.actor_name;
  if (entry.actor_email) return entry.actor_email;
  return entry.actor_type === 'system' ? 'System' : 'Unknown';
};

/**
 * One row in the trail. Collapsed it answers who/what/when; expanded it shows
 * the field-level diff and the request provenance.
 *
 * `showEntity` is off inside a single entity's own history, where repeating
 * the configuration name on every row is just noise.
 */
export const ActivityEntry = ({
  entry,
  showEntity = true,
}: {
  entry: ActivityLog;
  showEntity?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  const ActorIcon = ACTOR_ICON[entry.actor_type] ?? User;
  const changes = entry.changes ?? [];
  const hasDetail = changes.length > 0 || Boolean(entry.after) || Boolean(entry.before);

  return (
    <div className="rounded-lg border p-3 dark:border-gray-700">
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((prev) => !prev)}
        className="flex w-full items-start gap-3 text-left"
        disabled={!hasDetail}
      >
        <span className="mt-0.5 shrink-0">
          {hasDetail ? (
            expanded ? (
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            )
          ) : (
            <span className="block h-4 w-4" />
          )}
        </span>

        <span className="min-w-0 flex-1 space-y-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${actionTone(
                entry.action,
                entry.status,
              )}`}
            >
              {entry.status === 'failed' ? 'Rejected · ' : ''}
              {ACTIVITY_ACTION_LABELS[entry.action] ?? entry.action.replace(/_/g, ' ')}
            </span>

            {showEntity && (
              <span className="text-sm font-medium">
                {ACTIVITY_ENTITY_TYPE_LABELS[entry.entity_type] ?? entry.entity_type}
                {entry.entity_label ? ` · ${entry.entity_label}` : ''}
              </span>
            )}

            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                ACTOR_TONE[entry.actor_type]
              }`}
            >
              <ActorIcon className="h-3 w-3" />
              {actorLabel(entry)}
            </span>

            <span className="text-muted-foreground text-xs">{formatWhen(entry.created_at)}</span>
          </span>

          {entry.summary && (
            <span className="text-muted-foreground block text-xs">{entry.summary}</span>
          )}

          {entry.error && (
            <span className="block text-xs text-amber-600 dark:text-amber-400">{entry.error}</span>
          )}
        </span>
      </button>

      {expanded && (
        <div className="mt-3 border-t pt-3 dark:border-gray-700">
          <ActivityDiff changes={changes} />

          <dl className="text-muted-foreground mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] sm:grid-cols-4">
            {entry.actor_email && (
              <div>
                <dt className="font-semibold">Account</dt>
                <dd className="break-all">{entry.actor_email}</dd>
              </div>
            )}
            {entry.source && (
              <div>
                <dt className="font-semibold">Source</dt>
                <dd>{ACTIVITY_SOURCE_LABELS[entry.source] ?? entry.source}</dd>
              </div>
            )}
            {entry.ip && (
              <div>
                <dt className="font-semibold">IP</dt>
                <dd className="font-mono">{entry.ip}</dd>
              </div>
            )}
            {entry.path && (
              <div>
                <dt className="font-semibold">Request</dt>
                <dd className="font-mono break-all">
                  {entry.method} {entry.path}
                </dd>
              </div>
            )}
          </dl>

          {changes.length === 0 && entry.after !== null && entry.after !== undefined && (
            <div className="mt-3">
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase">
                Submitted payload
              </p>
              <pre className="bg-muted max-h-64 overflow-auto rounded-md p-2 text-[11px]">
                {JSON.stringify(entry.after, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
